import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  LinearProgress,
} from '@mui/material';
import {
  Add,
  CloudUpload,
  Edit,
  Delete,
  Download,
  Share,
  Visibility,
  Movie,
  Image as ImageIcon,
  Article,
  Palette,
  VideoCall,
  AudioFile,
  MoreVert,
} from '@mui/icons-material';
import { brandColors } from '../../theme';

interface ContentItem {
  id: number;
  name: string;
  type: 'image' | 'video' | 'text' | 'design';
  status: 'draft' | 'review' | 'approved' | 'published';
  thumbnail: string;
  createdAt: string;
  campaign?: string;
  channels: string[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

const ContentCreation: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [newContentOpen, setNewContentOpen] = useState(false);
  const [contentType, setContentType] = useState('');

  const steps = [
    'Content-Typ wählen',
    'Design & Erstellung',
    'Review & Freigabe',
    'Kanalverteilung'
  ];

  const contentItems: ContentItem[] = [
    {
      id: 1,
      name: 'Sommer Handwerker Banner',
      type: 'image',
      status: 'approved',
      thumbnail: '/api/placeholder/300/200',
      createdAt: '15.09.2025',
      campaign: 'Sommer Kampagne',
      channels: ['Meta', 'Google Ads']
    },
    {
      id: 2,
      name: 'Herbst Promotion Video',
      type: 'video',
      status: 'review',
      thumbnail: '/api/placeholder/300/200',
      createdAt: '14.09.2025',
      campaign: 'Herbst Promotion',
      channels: ['Meta', 'YouTube']
    },
    {
      id: 3,
      name: 'Weihnachts Special Text',
      type: 'text',
      status: 'draft',
      thumbnail: '/api/placeholder/300/200',
      createdAt: '13.09.2025',
      campaign: 'Weihnachts Special',
      channels: ['Meta']
    },
    {
      id: 4,
      name: 'Neujahr Grafik Design',
      type: 'design',
      status: 'published',
      thumbnail: '/api/placeholder/300/200',
      createdAt: '12.09.2025',
      campaign: 'Neujahr Angebote',
      channels: ['Print', 'Meta']
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'var(--success)';
      case 'approved': return 'var(--brand)';
      case 'review': return 'var(--warning)';
      default: return 'var(--slate-600)';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'published': return 'Veröffentlicht';
      case 'approved': return 'Freigegeben';
      case 'review': return 'In Review';
      default: return 'Entwurf';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'video': return <Movie />;
      case 'image': return <ImageIcon />;
      case 'text': return <Article />;
      case 'design': return <Palette />;
      default: return <ImageIcon />;
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleNewContentOpen = () => {
    setNewContentOpen(true);
  };

  const handleNewContentClose = () => {
    setNewContentOpen(false);
    setActiveStep(0);
    setContentType('');
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const contentTemplates = [
    { type: 'social-post', name: 'Social Media Post', icon: <Share />, description: 'Instagram & Facebook Posts' },
    { type: 'banner', name: 'Web Banner', icon: <ImageIcon />, description: 'Google Ads & Display Banner' },
    { type: 'video-ad', name: 'Video Werbung', icon: <VideoCall />, description: 'Kurze Werbevideos für Social Media' },
    { type: 'print-ad', name: 'Print Anzeige', icon: <Article />, description: 'Zeitungs- und Magazinanzeigen' },
    { type: 'radio-spot', name: 'Radio Spot', icon: <AudioFile />, description: 'Audio-Werbung für Radio' },
  ];

  return (
    <Box>
      {/* Header Section (neutral) */}
      <Box sx={{ mb: 4, p: 3, borderRadius: 'var(--radius)', border: '1px solid var(--line)', backgroundColor: 'var(--card)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h2" sx={{ fontWeight: 600, mb: 1 }}>
              Content Creation
            </Typography>
            <Typography variant="body1" sx={{ color: 'var(--muted)' }}>
              Erstellen Sie professionelle Inhalte für alle Kanäle
            </Typography>
          </Box>
          <Button variant="contained" size="large" startIcon={<Add />} onClick={handleNewContentOpen}>
            Neuer Content
          </Button>
        </Box>
      </Box>
      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card sx={{ border: '1px solid var(--line)', backgroundColor: 'var(--card)', borderRadius: 'var(--radius)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: brandColors.midnightSteel }}>
                    24
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Gesamt Content
                  </Typography>
                </Box>
                <ImageIcon sx={{ fontSize: 40, color: 'var(--brand)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card sx={{ border: '1px solid var(--line)', backgroundColor: 'var(--card)', borderRadius: 'var(--radius)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: brandColors.midnightSteel }}>
                    8
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    In Review
                  </Typography>
                </Box>
                <Visibility sx={{ fontSize: 40, color: 'var(--brand)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card sx={{ border: '1px solid var(--line)', backgroundColor: 'var(--card)', borderRadius: 'var(--radius)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: brandColors.midnightSteel }}>
                    16
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Veröffentlicht
                  </Typography>
                </Box>
                <Share sx={{ fontSize: 40, color: 'var(--brand)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card sx={{ border: '1px solid var(--line)', backgroundColor: 'var(--card)', borderRadius: 'var(--radius)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: brandColors.midnightSteel }}>
                    5
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Templates
                  </Typography>
                </Box>
                <Palette sx={{ fontSize: 40, color: 'var(--brand)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Content Overview */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Alle Inhalte" />
            <Tab label="Bilder" />
            <Tab label="Videos" />
            <Tab label="Texte" />
            <Tab label="Templates" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 3 }}>
            <ImageList cols={4} gap={16} sx={{ mb: 0 }}>
              {contentItems.map((item) => (
                <ImageListItem key={item.id}>
                  <Card sx={{ height: '100%' }}>
                    <Box sx={{ 
                      height: 180, 
                      bgcolor: '#f5f5f5', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      position: 'relative'
                    }}>
                      {getTypeIcon(item.type)}
                      <IconButton 
                        sx={{ 
                          position: 'absolute', 
                          top: 8, 
                          right: 8, 
                          bgcolor: 'white',
                          '&:hover': { bgcolor: 'white' }
                        }}
                        size="small"
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        {item.name}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Chip 
                          label={getStatusText(item.status)} 
                          size="small"
                          sx={{ 
                            bgcolor: `${getStatusColor(item.status)}20`,
                            color: getStatusColor(item.status),
                            fontWeight: 600
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {item.createdAt}
                        </Typography>
                      </Box>
                      {item.campaign && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {item.campaign}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {item.channels.map((channel) => (
                          <Chip key={channel} label={channel} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Bild Content</Typography>
            <ImageList cols={3} gap={16}>
              {contentItems.filter(item => item.type === 'image').map((item) => (
                <ImageListItem key={item.id}>
                  <Card>
                    <Box sx={{ height: 200, bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ImageIcon sx={{ fontSize: 60, color: '#ccc' }} />
                    </Box>
                    <ImageListItemBar title={item.name} subtitle={item.campaign} />
                  </Card>
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Video Content</Typography>
            <ImageList cols={3} gap={16}>
              {contentItems.filter(item => item.type === 'video').map((item) => (
                <ImageListItem key={item.id}>
                  <Card>
                    <Box sx={{ height: 200, bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Movie sx={{ fontSize: 60, color: '#ccc' }} />
                    </Box>
                    <ImageListItemBar title={item.name} subtitle={item.campaign} />
                  </Card>
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Text Content</Typography>
            <List>
              {contentItems.filter(item => item.type === 'text').map((item) => (
                <ListItem key={item.id} sx={{ border: '1px solid #f0f0f0', borderRadius: 2, mb: 1 }}>
                  <ListItemText
                    primary={item.name}
                    secondary={`${item.campaign} • ${item.createdAt}`}
                  />
                  <ListItemSecondaryAction>
                    <Chip 
                      label={getStatusText(item.status)} 
                      size="small"
                      sx={{ 
                        bgcolor: `${getStatusColor(item.status)}20`,
                        color: getStatusColor(item.status),
                        mr: 1
                      }}
                    />
                    <IconButton>
                      <Edit />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Content Templates</Typography>
            <Grid container spacing={3}>
              {contentTemplates.map((template) => (
                <Grid
                  key={template.type}
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 4
                  }}>
                  <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }}>
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <Box sx={{ 
                        width: 80, 
                        height: 80, 
                        borderRadius: 2,
                        bgcolor: `${brandColors.electricTeal}10`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2
                      }}>
                        {React.cloneElement(template.icon, { 
                          sx: { fontSize: 40, color: brandColors.electricTeal } 
                        })}
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {template.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {template.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>
      </Card>
      {/* New Content Creation Dialog */}
      <Dialog open={newContentOpen} onClose={handleNewContentClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ bgcolor: brandColors.electricTeal, color: brandColors.clearLight }}>
          Neuen Content erstellen
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3 }}>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {activeStep === 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 3 }}>Content-Typ auswählen</Typography>
                <Grid container spacing={3}>
                  {contentTemplates.map((template) => (
                    <Grid
                      key={template.type}
                      size={{
                        xs: 12,
                        sm: 6,
                        md: 4
                      }}>
                      <Card 
                        sx={{ 
                          cursor: 'pointer', 
                          border: contentType === template.type ? `2px solid ${brandColors.electricTeal}` : '1px solid #e0e0e0',
                          '&:hover': { boxShadow: 4 } 
                        }}
                        onClick={() => setContentType(template.type)}
                      >
                        <CardContent sx={{ textAlign: 'center', p: 3 }}>
                          <Box sx={{ 
                            width: 60, 
                            height: 60, 
                            borderRadius: 2,
                            bgcolor: contentType === template.type ? `${brandColors.electricTeal}20` : '#f5f5f5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2
                          }}>
                            {React.cloneElement(template.icon, { 
                              sx: { 
                                fontSize: 32, 
                                color: contentType === template.type ? brandColors.electricTeal : '#666' 
                              } 
                            })}
                          </Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {template.name}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {activeStep === 1 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 3 }}>Design & Erstellung</Typography>
                <Grid container spacing={3}>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <Card sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9f9f9' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <CloudUpload sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                          Design-Bereich
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Hier würde der Content-Editor erscheinen
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <Box>
                      <TextField fullWidth label="Content Titel" sx={{ mb: 2 }} />
                      <TextField fullWidth label="Beschreibung" multiline rows={4} sx={{ mb: 2 }} />
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Kampagne zuweisen</InputLabel>
                        <Select label="Kampagne zuweisen">
                          <MenuItem value="sommer">Sommer Handwerker Kampagne</MenuItem>
                          <MenuItem value="herbst">Herbst Promotion 2025</MenuItem>
                          <MenuItem value="weihnachten">Weihnachts Special</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl fullWidth>
                        <InputLabel>Ziel-Kanäle</InputLabel>
                        <Select multiple label="Ziel-Kanäle" defaultValue={[]}>
                          <MenuItem value="meta">Meta (Facebook/Instagram)</MenuItem>
                          <MenuItem value="google">Google Ads</MenuItem>
                          <MenuItem value="print">Print Medien</MenuItem>
                          <MenuItem value="radio">Radio</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            {activeStep === 2 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 3 }}>Review & Freigabe</Typography>
                <Paper sx={{ p: 3, bgcolor: '#f9f9f9' }}>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Content wird automatisch zur Review weitergeleitet.
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText primary="Brand Guidelines Check" secondary="Automatische Überprüfung der Markenrichtlinien" />
                      <Chip label="✓ Bestanden" color="success" size="small" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Qualitätskontrolle" secondary="Technische und inhaltliche Prüfung" />
                      <LinearProgress sx={{ width: 200 }} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Rechtliche Prüfung" secondary="Compliance und rechtliche Aspekte" />
                      <Chip label="Ausstehend" color="warning" size="small" />
                    </ListItem>
                  </List>
                </Paper>
              </Box>
            )}

            {activeStep === 3 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 3 }}>Kanalverteilung</Typography>
                <Grid container spacing={3}>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                      Ausgewählte Kanäle
                    </Typography>
                    <List>
                      <ListItem sx={{ border: '1px solid #e0e0e0', borderRadius: 2, mb: 1 }}>
                        <ListItemText primary="Meta (Facebook/Instagram)" secondary="Automatische Anpassung für verschiedene Formate" />
                        <Chip label="Bereit" color="success" size="small" />
                      </ListItem>
                      <ListItem sx={{ border: '1px solid #e0e0e0', borderRadius: 2, mb: 1 }}>
                        <ListItemText primary="Google Ads" secondary="Display & Search Network Optimierung" />
                        <Chip label="Bereit" color="success" size="small" />
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                      Zeitplanung
                    </Typography>
                    <TextField fullWidth label="Veröffentlichungsdatum" type="datetime-local" sx={{ mb: 2 }} InputLabelProps={{ shrink: true }} />
                    <FormControl fullWidth>
                      <InputLabel>Veröffentlichungstyp</InputLabel>
                      <Select label="Veröffentlichungstyp">
                        <MenuItem value="immediate">Sofort veröffentlichen</MenuItem>
                        <MenuItem value="scheduled">Geplante Veröffentlichung</MenuItem>
                        <MenuItem value="manual">Manuelle Freigabe</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleNewContentClose}>Abbrechen</Button>
          <Button disabled={activeStep === 0} onClick={handleBack}>
            Zurück
          </Button>
          <Button 
            variant="contained" 
            onClick={activeStep === steps.length - 1 ? handleNewContentClose : handleNext}
            sx={{ 
              bgcolor: brandColors.electricTeal,
              '&:hover': { bgcolor: brandColors.midnightSteel }
            }}
          >
            {activeStep === steps.length - 1 ? 'Content erstellen' : 'Weiter'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContentCreation;
